import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPinOff, Navigation, MapPin } from 'lucide-react';
import Header from '../Header';
import MosqueList from '../MosqueList';
import LocationFilter, { getLocationDisplayName } from '../LocationFilter/LocationFilter';
import DishFilter from '../DishFilter/DishFilter';
import Footer from '../Footer';
import AddMosqueButton from '../AddMosqueButton';
import MosqueLoader from '../../utils/MosqueLoader';
import { publicRequest } from '../../requestMethods';
import { getDishDisplayName } from '../../utils/dishDisplay';
import CitySearch from '../CitySearch/CitySearch';
import RoutePlanner from '../RoutePlanner/RoutePlanner';
import PopularDestinations from '../PopularDestinations/PopularDestinations';
import WhyChorullaPalli from '../WhyChorullaPalli/WhyChorullaPalli';
import './HomePage.scss';
import { Search } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showAllMosques = searchParams.get('explore') === 'all';
  
  const [mosques, setMosques] = useState([]);
  const [filteredMosques, setFilteredMosques] = useState([]);
  const [displayedMosques, setDisplayedMosques] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDish, setSelectedDish] = useState("");
  const [userDistrict, setUserDistrict] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'route'

  const MOSQUES_PER_PAGE = 20;

  useEffect(() => {
    let filtered = mosques;

    if (searchTerm) {
      filtered = filtered.filter(mosque =>
        mosque.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mosque.district.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedDistrict) {
      filtered = filtered.filter(mosque =>
        selectedCountry === 'India' && selectedState
          ? mosque.state === selectedState && mosque.district === selectedDistrict
          : mosque.district === selectedDistrict
      );
    }
    if (selectedDish) {
      filtered = filtered.filter(mosque => getDishDisplayName(mosque.dish) === selectedDish);
    }

    setFilteredMosques(filtered);
    setDisplayedMosques(filtered.slice(0, MOSQUES_PER_PAGE));
    setCurrentPage(1);
  }, [selectedDistrict, selectedDish, searchTerm, mosques, selectedCountry, selectedState]);

  useEffect(() => {
    // Only fetch mosques if explore=all is in URL
    if (showAllMosques) {
      const fetchMosques = async () => {
        setIsLoading(true);
        try {
          const response = await publicRequest.get("/mosques/get-all-mosques");
          setMosques(response.data);
          setFilteredMosques(response.data);
          setDisplayedMosques(response.data.slice(0, MOSQUES_PER_PAGE));
        } catch (error) {
          setError("Failed to fetch mosques. Please try again.");
        } finally {
          setIsLoading(false);
          setLoading(false);
        }
      };

      fetchMosques();
    }
  }, [showAllMosques]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    const start = 0;
    const end = nextPage * MOSQUES_PER_PAGE;
    setDisplayedMosques(filteredMosques.slice(start, end));
    setCurrentPage(nextPage);
  };

  const handleShowNearby = () => {
    setIsLocating(true);
    setLocationError(null);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setIsLocating(false);
          await fetchUserLocation(latitude, longitude);
          setUserLocation({ latitude, longitude });
          navigate('/?explore=all');
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationError("Could not get your location");
          setIsLocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      setLocationError("Location is not supported by your browser");
      setIsLocating(false);
    }
  };

  const fetchUserLocation = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
      );
      const data = await response.json();
      const district = data.address?.county || data.address?.state_district || "";
      setUserDistrict(district);
      setSelectedDistrict(district);
    } catch (err) {
      setError("Could not determine location.");
    }
  };

  const handleAddMosque = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      navigate('/add-mosque');
    } else {
      navigate('/login', { state: { from: '/add-mosque' } });
    }
  };

  const RamadanStats = ({ stats }) => (
    <div className="ramadan-stats">
      <h3>Ramadan 2024 Impact</h3>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.totalMosques}</div>
          <div className="stat-label">Participating Mosques</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalDistricts}</div>
          <div className="stat-label">Districts Covered</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalVisits}</div>
          <div className="stat-label">Total Visits</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalContributions}</div>
          <div className="stat-label">Community Contributors</div>
        </div>
      </div>
    </div>
  );

  const RamadanEndBanner = () => (
    <div className="thank-you-banner">
      <div className="banner-content">
        <div className="banner-header">
          <span className="banner-icon">🌙</span>
          <h2>Ramadan 2025 Has Concluded</h2>
        </div>

        <p className="thank-you-message">
          Alhamdulillah, thank you for being part of our blessed journey during Ramadan.
          Your support helped connect our community through food sharing and compassion.
        </p>

        <div className="stay-connected">
          <h3>Stay Connected</h3>
          <p>
            Follow us for updates on special events and next Ramadan's food services.
            The spirit of sharing continues throughout the year.
          </p>
          <div className="contact-info">
            <span>📧 hi@chorullapalli.com</span>
            {/* <span>📱 Updates on WhatsApp</span> */}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app">
      <Header />
      {isLoading && <MosqueLoader />}

      {!showAllMosques ? (
        // Landing page view
        <main className="homepage">
          {/* Hero Section */}
          <section className="homepage__hero">
            <div className="homepage__hero-content">
              <h1 className="homepage__hero-title">
                Find the <span>mosques</span> offering Iftar meals
              </h1>
              <p className="homepage__hero-caption">
                Your guide to discovering mosques serving Iftar meals during Ramadan across Kerala. 
                Connect with your community and find nearby mosques offering delicious meals.
              </p>
              <div className="homepage__hero-search">
                <div className="homepage__search-tabs">
                  <button
                    className={`homepage__tab ${activeTab === 'search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('search')}
                  >
                    Search City
                  </button>
                  <button
                    className={`homepage__tab ${activeTab === 'route' ? 'active' : ''}`}
                    onClick={() => setActiveTab('route')}
                  >
                    Route Planner
                  </button>
                </div>
                <div className="homepage__search-content">
                  {activeTab === 'search' ? (
                    <CitySearch />
                  ) : (
                    <RoutePlanner />
                  )}
                </div>
              </div>
              <div className="homepage__hero-buttons">
                <button
                  className="homepage__explore-all"
                  onClick={() => navigate('/?explore=all')}
                >
                  <MapPin size={18} />
                  Explore all mosque
                </button>
                <button
                  className={`homepage__nearby ${isLocating ? 'locating' : ''}`}
                  onClick={handleShowNearby}
                  disabled={isLocating}
                >
                  {isLocating ? (
                    <>
                      <Navigation size={18} className="spinning" />
                      Finding...
                    </>
                  ) : (
                    <>
                      <Navigation size={18} />
                      Nearby
                    </>
                  )}
                </button>
              </div>
              {locationError && !showAllMosques && (
                <p className="homepage__location-error">{locationError}</p>
              )}
            </div>
          </section>

          {/* Popular Destinations */}
          {/* <PopularDestinations /> */}

          {/* Why Chorulla Palli */}
          <WhyChorullaPalli />
        </main>
      ) : (
        // Explore all mosques view
        <main className="app__main">
        {/* Location Section */}

        <button
          className={`nearby-button ${isLocating ? 'locating' : ''}`}
          onClick={handleShowNearby}
          disabled={isLocating}
        >
          {isLocating ? (
            <span className="locating-text">
              <Navigation className="icon spinning" />
              Getting Location...
            </span>
          ) : (
            <span className="default-text">
              <Navigation className="icon" />
              Show Nearby
            </span>
          )}
        </button>

        {locationError && (
          <div className="location-error">
            <MapPinOff className="icon" />
            {locationError}
          </div>
        )}

        {userDistrict && (
          <section className="app__section app__section--floating-button">
            <div className="location-container">


              {userDistrict && (
                <div className="current-location">
                  Your Location: <strong>{userDistrict}</strong>
                </div>
              )}
            </div>
          </section>
        )}


        {/* Filters Section */}
        <section className="app__section app__section--filters">
          <div className="filters-container">
            <div className="filters-header">
              <div className="filters-header__main">




                <h2>Find Mosques</h2>
                {selectedCountry && (
                  <p className="selected-location">
                    {getLocationDisplayName(selectedCountry, selectedDistrict, '', '', selectedState)}
                  </p>
                )}
              </div>

              {/* Move search bar here */}

            </div>

            <div className="filters-grid">
              <div className="filter-group">
                <label>Location</label>
                <LocationFilter
                  selectedCountry={selectedCountry}
                  selectedDistrict={selectedDistrict}
                  selectedState={selectedState}
                  onCountryChange={setSelectedCountry}
                  onDistrictChange={setSelectedDistrict}
                  onStateChange={setSelectedState}
                />
              </div>

              <div className="filter-group">
                <label>Food Type</label>
                <DishFilter
                  selectedDish={selectedDish}
                  onDishChange={setSelectedDish}
                />
              </div>
            </div>


          </div>
        </section>

        {/* <section className="app__section app__section--results">
          <div className="search-bar">
            <Search className="search-bar__icon" size={18} />
            <input
              type="text"
              placeholder="Search mosques..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="search-bar__clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </section> */}
        {/* Results Section */}
        <section className="mosque__cards">
          {error ? (
            <div className="app__error">{error}</div>
          ) : (
            <>
              <div className="results-header">
                <div className="results-summary">
                  <div className="stats-card">
                    <div className="stats-card__content">
                      <span className="stats-card__number">{filteredMosques.length}</span>
                      <span className="stats-card__label">Mosques Listed</span>
                    </div>
                    <div className="stats-card__icon">🕌</div>
                  </div>
                  {/* <h3>Available Mosques</h3> */}
                </div>
                <div className="results-filters">
                  {selectedDistrict && (
                    <span className="filter-tag">
                      📍 {selectedCountry === 'India' && selectedState ? `${selectedDistrict}, ${selectedState}` : selectedDistrict}
                    </span>
                  )}
                  {selectedDish && (
                    <span className="filter-tag">
                      🍽️ {selectedDish}
                    </span>
                  )}
                </div>
              </div>

              <MosqueList mosques={displayedMosques} loading={loading} />

              {filteredMosques.length > displayedMosques.length && (
                <div className="load-more">
                  <button onClick={handleLoadMore} className="load-more__button">
                    Show More Mosques
                  </button>
                </div>
              )}
            </>
          )}
        </section>
        </main>
      )}

      {/* Floating Add Mosque button (bottom-right) */}
      <AddMosqueButton onClick={handleAddMosque} />

      <Footer />
    </div>
  );
};

export default HomePage;